import { Cart, CartItem, ProductListItem } from '@/types';
import { router } from '@inertiajs/react';
import { route } from 'ziggy-js';
import { create } from 'zustand';

export type AddToCartFn = (
    product: ProductListItem,
    handleBefore?: () => void,
    handleFinish?: () => void,
) => void;

export type repeatOrderToCartFn = (
    orderId: string,
    shouldReplace: boolean,
    handleSuccess?: () => void,
    handleBefore?: () => void,
    handleFinish?: () => void,
) => void;

export type updateQuantityFn = (
    itemId: number,
    quantity: number,
    handleBefore?: () => void,
    handleFinish?: () => void,
) => void;

export interface CartState {
    // Данные корзины
    cartItems: CartItem[];
    cart?: Cart;
    cartAmount: string;
    cartCount: number;
    isOpen: boolean;

    // Действия
    initializeCart: (cart?: Cart, cartItems?: CartItem[]) => void;
    toggleOpen: () => void;
    onCloseCart: () => void;
    addToCart: AddToCartFn;
    repeatOrderToCart: repeatOrderToCartFn;
    updateQuantity: updateQuantityFn;
    removeItem: (itemId: number) => void;
    clearCart: () => void;
    isVariationInCart: (product_variation_id: number) => boolean;
    getCartItem: (product_variation_id: number) => CartItem | undefined;
    updateCartItems: (cartItems: CartItem[]) => void;
}

export const useCartStore = create<CartState>((set, get) => ({
    // Начальное состояние
    cartItems: [],
    cart: undefined,
    cartAmount: '0 ₽',
    cartCount: 0,
    isOpen: false,

    // Инициализация корзины данными с сервера
    initializeCart: (cart?: Cart, cartItems?: CartItem[]) => {
        const actualCartItems = cart?.items || cartItems || [];
        const cartAmount = cart?.amount || '0 ₽';
        const cartCount = cart?.count || actualCartItems.length;

        set({
            cartItems: actualCartItems,
            cart,
            cartAmount,
            cartCount,
        });
    },

    // Переключение состояния открытия корзины
    toggleOpen: () => {
        set((state) => ({
            isOpen: !state.isOpen,
        }));
    },

    // Закрытие корзины
    onCloseCart: () => {
        set({
            isOpen: false,
        });
    },

    // Добавление товара в корзину
    addToCart: (
        product: ProductListItem,
        handleBefore?: () => void,
        handleFinish?: () => void,
    ) => {
        router.get(
            route('cart.add', {
                productVariation: product.id,
                quantity: product?.packHalfQuantity,
            }),
            {},
            {
                async: true,
                preserveScroll: true,
                preserveState: true,
                only: ['cart', 'cartItems'],
                onBefore: () => handleBefore?.(),
                onFinish: () => {
                    handleFinish?.();
                    window.Alpine.store('cart').update();
                },
            },
        );
    },

    // Повторить заказ
    repeatOrderToCart: (
        orderId,
        shouldReplace = false,
        handleSuccess?: () => void,
        handleBefore?: () => void,
        handleFinish?: () => void,
    ) => {
        // Получаем маршрут динамически
        router.get(
            route('cart.repeatOrder', { id: String(orderId) }),
            { replace: shouldReplace },
            {
                preserveState: true,
                preserveScroll: true,
                only: ['cart', 'cartItems'],
                onSuccess: () => handleSuccess?.(),
                onBefore: () => handleBefore?.(),
                onFinish: () => handleFinish?.(),
            },
        );
    },

    // Обновление количества товара
    updateQuantity: (
        itemId: number,
        quantity: number,
        handleBefore?: () => void,
        handleFinish?: () => void,
    ) => {
        router.patch(
            route('cart.quantity', { cartItem: itemId }),
            { quantity },
            {
                async: true,
                preserveScroll: true,
                only: ['cart', 'cartItems'],
                onBefore: () => handleBefore?.(),
                onFinish: () => handleFinish?.(),
            },
        );
    },

    // Удаление товара из корзины
    removeItem: (itemId: number) => {
        router.delete(route('cart.delete', { item: itemId }), {
            async: true,
            preserveScroll: true,
            only: ['cart', 'cartItems'],
        });
    },

    // Очистка корзины
    clearCart: () => {
        router.delete(route('cart.truncate'), {
            async: true,
            preserveScroll: true,
            only: ['cart', 'cartItems'],
        });
    },

    // Получение item корзины по product_variation_id
    getCartItem: (product_variation_id: number): CartItem | undefined => {
        const { cartItems } = get();
        return cartItems.find(
            (item) => item.productVariation.id === product_variation_id,
        );
    },

    updateCartItems: (cartItems: CartItem[]) => {
        set((state) => ({
            cartItems: cartItems,
        }));
    },

    // Проверка, находится ли товар в корзине
    isVariationInCart: (product_variation_id: number): boolean => {
        return get().cartItems.some(
            (item) => item.productVariation.id === product_variation_id,
        );
    },
}));

// Хук для удобного использования store
export const useCart = () => {
    const store = useCartStore();
    return {
        cartItems: store.cartItems,
        cart: store.cart,
        cartAmount: store.cartAmount,
        cartCount: store.cartCount,
        isOpen: store.isOpen,
        addToCart: store.addToCart,
        repeatOrderToCart: store.repeatOrderToCart,
        updateQuantity: store.updateQuantity,
        removeItem: store.removeItem,
        clearCart: store.clearCart,
        toggleOpen: store.toggleOpen,
        onCloseCart: store.onCloseCart,
        handleIsInCart: store.isVariationInCart,
        getCartItem: store.getCartItem,
        updateCartItems: store.updateCartItems,
        initializeCart: store.initializeCart,
    };
};

export default useCartStore;
